import { useState, useEffect } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Folder, Users, MagnifyingGlass, Image as ImageIcon, FilePdf, Plus, ChartLineUp, Trash
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Projetos() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createStep, setCreateStep] = useState("type"); // "type" | "pick_client"
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchClientsWithProjects();
    fetchProposals();
  }, []);

  const fetchClientsWithProjects = async () => {
    try {
      const response = await axios.get(`${API}/clients-with-projects`);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients with projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProposals = async () => {
    try {
      const response = await axios.get(`${API}/investment-proposals`);
      setProposals(response.data);
    } catch (error) {
      console.error("Error fetching investment proposals:", error);
    }
  };

  const handleClientSearchChange = async (value) => {
    setClientSearch(value);
    if (value.trim().length < 2) {
      setClientResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/clients?search=${encodeURIComponent(value)}`);
      setClientResults(response.data.slice(0, 8));
    } catch (error) {
      console.error("Error searching clients:", error);
    }
  };

  const resetCreateDialog = () => {
    setShowCreateDialog(false);
    setCreateStep("type");
    setClientSearch("");
    setClientResults([]);
  };

  const handleCreateInvestmentProposal = async () => {
    setCreating(true);
    try {
      const todayLabel = new Date().toLocaleDateString('pt-PT');
      const response = await axios.post(`${API}/investment-proposals`, { name: `Nova Proposta - ${todayLabel}` });
      resetCreateDialog();
      navigate(`/projetos/propostas/${response.data.id}`);
    } catch (error) {
      console.error("Error creating investment proposal:", error);
      toast.error("Erro ao criar proposta de investimento");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProposal = async (e, proposalId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Eliminar esta Proposta de Investimento?")) return;
    try {
      await axios.delete(`${API}/investment-proposals/${proposalId}`);
      toast.success("Proposta eliminada");
      fetchProposals();
    } catch (error) {
      console.error("Error deleting proposal:", error);
      toast.error("Erro ao eliminar proposta");
    }
  };

  const filteredClients = clients.filter(client => 
    client.name.toLowerCase().includes(search.toLowerCase()) ||
    client.city?.toLowerCase().includes(search.toLowerCase())
  );

  const getTotalFiles = (client) => {
    let total = 0;
    if (client.projects) {
      client.projects.forEach(project => {
        total += (project.files?.length || 0);
      });
    }
    return total;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 md:pb-6" data-testid="projetos-page">
      {/* Header */}
      <PageHeader
        icon={Folder}
        title="Projetos"
        subtitle="Clientes com projetos registados e Propostas de Investimento"
        actions={
          <Button onClick={() => setShowCreateDialog(true)} className="bg-[#009392] hover:bg-[#007a7a] text-white" data-testid="btn-criar-projeto">
            <Plus size={18} /> Criar Projeto
          </Button>
        }
      />

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input
          placeholder="Pesquisar cliente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-projects"
        />
      </div>

      {/* Clients List */}
      {filteredClients.length === 0 ? (
        <div className="empty-state py-16 bg-stone-50 dark:bg-stone-900 rounded-xl">
          <Folder size={64} className="text-stone-300 dark:text-stone-600 mb-4" />
          <h3 className="text-lg font-semibold text-stone-700 dark:text-stone-300 mb-2">
            {search ? "Nenhum resultado encontrado" : "Sem projetos"}
          </h3>
          <p className="text-stone-500 dark:text-stone-400 max-w-md mx-auto">
            {search 
              ? "Tente uma pesquisa diferente"
              : "Adicione projetos aos clientes para os ver aqui"
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <Link
              key={client.id}
              to={`/clients/${client.id}?tab=projetos`}
              className="gresart-card hover:shadow-lg transition-all group"
              data-testid={`project-client-${client.id}`}
            >
              <div className="flex items-start gap-4">
                {client.logo_url ? (
                  <img 
                    src={client.logo_url} 
                    alt={client.name}
                    className="w-16 h-16 rounded-lg object-contain bg-stone-100"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center">
                    <Users size={28} className="text-stone-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-stone-900 dark:text-white truncate group-hover:text-[#00A19A] transition-colors">
                    {client.name}
                  </h3>
                  {client.city && (
                    <p className="text-sm text-stone-500 dark:text-stone-400">{client.city}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-stone-500">
                    <span className="flex items-center gap-1">
                      <Folder size={14} />
                      {client.projects?.length || 0} {(client.projects?.length || 0) === 1 ? 'projeto' : 'projetos'}
                    </span>
                    <span className="flex items-center gap-1">
                      <ImageIcon size={14} />
                      {getTotalFiles(client)} ficheiros
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Propostas de Investimento */}
      {proposals.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-stone-800 flex items-center gap-2">
            <ChartLineUp size={18} className="text-[#009392]" />
            Propostas de Investimento
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {proposals.map((proposal) => (
              <Link
                key={proposal.id}
                to={`/projetos/propostas/${proposal.id}`}
                className="gresart-card hover:shadow-lg transition-all group relative"
                data-testid={`investment-proposal-${proposal.id}`}
              >
                <button
                  onClick={(e) => handleDeleteProposal(e, proposal.id)}
                  className="absolute top-3 right-3 text-stone-300 hover:text-red-500 transition-colors"
                  data-testid={`delete-proposal-${proposal.id}`}
                >
                  <Trash size={16} />
                </button>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-lg bg-[#009392]/10 flex items-center justify-center flex-shrink-0">
                    <ChartLineUp size={22} className="text-[#009392]" />
                  </div>
                  <div className="flex-1 min-w-0 pr-5">
                    <h3 className="font-semibold text-stone-900 truncate group-hover:text-[#009392] transition-colors">
                      {proposal.name}
                    </h3>
                    <div className="flex items-center gap-3 mt-2 text-xs text-stone-500">
                      <span className="flex items-center gap-1">
                        <Users size={14} />
                        {proposal.client_count} {proposal.client_count === 1 ? 'cliente' : 'clientes'}
                      </span>
                      <span className="font-semibold text-[#009392]">
                        €{proposal.grand_total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Dialog: Criar Projeto */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => !open && resetCreateDialog()}>
        <DialogContent className="max-w-md" data-testid="criar-projeto-dialog">
          <DialogHeader>
            <DialogTitle>{createStep === "type" ? "Criar Projeto" : "Selecionar Cliente"}</DialogTitle>
          </DialogHeader>
          {createStep === "type" ? (
            <div className="space-y-3 pt-2">
              <button
                onClick={() => setCreateStep("pick_client")}
                className="w-full flex items-center gap-3 p-4 rounded-xl border border-stone-200 hover:border-[#009392] hover:bg-[#009392]/5 transition-colors text-left"
                data-testid="tipo-projeto-cliente"
              >
                <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                  <Folder size={20} className="text-stone-500" />
                </div>
                <div>
                  <p className="font-semibold text-stone-900">Projeto de Cliente</p>
                  <p className="text-xs text-stone-500">Galeria de ficheiros/imagens associada a um único cliente</p>
                </div>
              </button>
              <button
                onClick={handleCreateInvestmentProposal}
                disabled={creating}
                className="w-full flex items-center gap-3 p-4 rounded-xl border border-stone-200 hover:border-[#009392] hover:bg-[#009392]/5 transition-colors text-left disabled:opacity-60"
                data-testid="tipo-proposta-investimento"
              >
                <div className="w-10 h-10 rounded-lg bg-[#009392]/10 flex items-center justify-center flex-shrink-0">
                  <ChartLineUp size={20} className="text-[#009392]" />
                </div>
                <div>
                  <p className="font-semibold text-stone-900">Proposta de Investimento</p>
                  <p className="text-xs text-stone-500">Selecione painéis/expositores do catálogo PPM para múltiplos clientes e gere um PDF consolidado</p>
                </div>
              </button>
            </div>
          ) : (
            <div className="space-y-3 pt-2">
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  autoFocus
                  placeholder="Pesquisar cliente pelo nome ou cidade..."
                  value={clientSearch}
                  onChange={(e) => handleClientSearchChange(e.target.value)}
                  className="pl-10"
                  data-testid="input-pick-client-search"
                />
              </div>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {clientResults.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/clients/${c.id}?tab=projetos`)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg hover:bg-stone-50 text-left transition-colors border border-stone-100"
                    data-testid={`pick-client-${c.id}`}
                  >
                    <span className="text-sm text-stone-700 truncate">{c.name}</span>
                    {c.city && <span className="text-xs text-stone-400 flex-shrink-0">{c.city}</span>}
                  </button>
                ))}
                {clientSearch.trim().length >= 2 && clientResults.length === 0 && (
                  <p className="text-sm text-stone-400 text-center py-4">Nenhum cliente encontrado</p>
                )}
              </div>
              <Button variant="outline" onClick={() => setCreateStep("type")} className="w-full" data-testid="btn-voltar-tipo-projeto">
                Voltar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
